import type { DeviceInfo, ViewingEvent, UserSession, ViewingEventData } from '../types/analytics';
import { getDeviceInfo } from './deviceInfo';

/**
 * Generate a clean UTC datetime string for Firebase
 */
export function cleanDateTimeUTC(): string {
  return new Date().toISOString();
}

/**
 * Generate a unique event ID for analytics events
 */
export function generateEventId(eventType: string, pieceId: string, userId: string): string {
  const timestamp = cleanDateTimeUTC().replace(/[-:.TZ]/g, '');
  const userIdShort = userId.substring(0, Math.min(6, userId.length));
  const pieceIdShort = pieceId.substring(0, Math.min(6, pieceId.length));
  return `${eventType}_${userIdShort}_${pieceIdShort}_${timestamp}`;
}

/**
 * Generate a unique session ID
 */
export function generateSessionId(userId: string): string {
  const timestamp = cleanDateTimeUTC();
  return `${userId}_${timestamp}`;
}

/**
 * Create viewing event data structure
 */
export function createViewingEvent(
  galleryId: string,
  userId: string,
  collectionId: string,
  pieceId: string,
  eventType: ViewingEvent['event_type'],
  sessionId: string,
  durationMs: number = 0
): ViewingEventData {
  const deviceInfo = getDeviceInfo();
  
  return {
    gallery_id: galleryId,
    user_id: userId,
    collection_id: collectionId,
    piece_id: pieceId,
    timestamp: cleanDateTimeUTC(),
    duration_ms: durationMs,
    event_type: eventType,
    session_id: sessionId,
    is_guided: false,
    device_info: {
      deviceType: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screenResolution: deviceInfo.screenResolution,
      mobile: deviceInfo.mobile.toString()
    }
  };
}

/**
 * Create session data structure
 */
export function createSessionData(
  userId: string,
  accessCode: string,
  sessionId: string
): Partial<UserSession> {
  const deviceInfo = getDeviceInfo();
  const now = cleanDateTimeUTC();
  
  return {
    Id: sessionId,
    AccessCode: accessCode,
    StartTime: now,
    LastUpdateTime: now,
    SessionDuration: 0,
    DeviceType: deviceInfo.deviceType,
    Platform: deviceInfo.os
  };
}

/**
 * Calculate session duration in seconds
 */
export function calculateSessionDuration(startTime: string, endTime?: string): number {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  return Math.floor((end.getTime() - start.getTime()) / 1000);
}

/**
 * Format duration in milliseconds to a human-readable string
 */
export function formatDuration(durationMs: number): string {
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Validate event data before sending to analytics
 */
export function validateEventData(eventData: ViewingEventData): boolean {
  const requiredFields = [
    'gallery_id',
    'user_id',
    'collection_id',
    'piece_id',
    'timestamp',
    'event_type',
    'session_id'
  ];

  return requiredFields.every(field => {
    const value = eventData[field as keyof ViewingEventData];
    return value !== null && value !== undefined && value !== '';
  });
}

/**
 * Create device info object for analytics
 */
export function createAnalyticsDeviceInfo(): Record<string, string> {
  const deviceInfo = getDeviceInfo();
  
  return {
    deviceType: deviceInfo.deviceType,
    deviceModel: 'Browser',
    operatingSystem: deviceInfo.os,
    processorType: 'Unknown',
    systemMemorySize: 'Unknown',
    graphicsDeviceName: 'Unknown',
    screenResolution: deviceInfo.screenResolution,
    platform: deviceInfo.os,
    browser: deviceInfo.browser,
    browserVersion: deviceInfo.browserVersion || 'Unknown'
  };
}